import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Bell,
  Globe,
  Landmark,
  Lock,
  RefreshCw,
  Shield,
  User,
  Users,
} from 'lucide-react';
import SoundPreferencesControls from '../components/settings/SoundPreferencesControls.jsx';
import SitePublicSettings from '../components/settings/SitePublicSettings.jsx';
import Nr2jBankSettings from '../components/settings/Nr2jBankSettings.jsx';
import Toast from '../components/Toast';
import {
  SettingsCard,
  SettingsPageHeader,
  SettingsReadOnlyField,
  SettingsTextField,
} from '../components/settings/settingsUi.jsx';
import { useAdminSessionUser } from '../hooks/useAdminSessionUser.js';
import { useToast } from '../hooks/useToast';
import apiService from '../services/api';
import {
  buildOwnAccountUpdatePayload,
  formatRoleLabel,
  getAuthenticatedIdentity,
  getSessionDisplayName,
  getSessionEmail,
  getSessionNom,
  getSessionPrenom,
  getSessionTelephone,
  isOwnAccountUpdatePayloadSafe,
  isValidLoginEmail,
  patchAdminSessionIdentity,
} from '../utils/settingsSessionUser.js';

const TABS = [
  { id: 'compte', label: 'Mon compte', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'securite', label: 'Sécurité', icon: Shield },
  { id: 'equipe', label: 'Équipe Clickorico', icon: Users },
  { id: 'site-public', label: 'Site public', icon: Globe },
  { id: 'nr2j-banque', label: 'Coordonnées bancaires', icon: Landmark },
];

function normalizePhone(value) {
  const raw = String(value || '').trim();
  return raw === 'N/A' ? '' : raw;
}

function AccountCard({ user }) {
  const identity = getAuthenticatedIdentity(user);
  const { toasts, toast, removeToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nom: getSessionNom(user),
    prenom: getSessionPrenom(user),
    telephone: normalizePhone(getSessionTelephone(user)),
    email: getSessionEmail(user),
  });

  useEffect(() => {
    if (editing) return;
    setForm({
      nom: getSessionNom(user),
      prenom: getSessionPrenom(user),
      telephone: normalizePhone(getSessionTelephone(user)),
      email: getSessionEmail(user),
    });
  }, [editing, user?.nom, user?.prenom, user?.telephone, user?.email]);

  useEffect(() => {
    const id = user?.id;
    if (!id) return undefined;
    let cancelled = false;
    apiService.getUser(id).then((fresh) => {
      if (cancelled || !fresh) return;
      const identityPatch = {
        nom: fresh.nom,
        prenom: fresh.prenom,
        telephone: normalizePhone(fresh.telephone),
      };
      if (fresh.email) identityPatch.email = fresh.email;
      patchAdminSessionIdentity(identityPatch);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const startEdit = () => {
    setForm({
      nom: getSessionNom(user),
      prenom: getSessionPrenom(user),
      telephone: normalizePhone(getSessionTelephone(user)),
      email: getSessionEmail(user),
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setForm({
      nom: getSessionNom(user),
      prenom: getSessionPrenom(user),
      telephone: normalizePhone(getSessionTelephone(user)),
      email: getSessionEmail(user),
    });
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!user?.id) {
      toast.error('Session incomplète : identifiant utilisateur absent.');
      return;
    }
    const payload = buildOwnAccountUpdatePayload(form);
    if (!payload.nom || !payload.prenom) {
      toast.error('Le nom et le prénom sont obligatoires.');
      return;
    }
    if (!isValidLoginEmail(payload.email)) {
      toast.error('Email de connexion invalide.');
      return;
    }
    if (!isOwnAccountUpdatePayloadSafe(payload)) {
      toast.error('Modification refusée.');
      return;
    }
    setSaving(true);
    try {
      const updated = await apiService.updateUser(user.id, payload);
      patchAdminSessionIdentity({
        nom: updated?.nom ?? payload.nom,
        prenom: updated?.prenom ?? payload.prenom,
        telephone: normalizePhone(updated?.telephone ?? payload.telephone),
        email: updated?.email ?? payload.email,
      });
      toast.success('Informations mises à jour');
      setEditing(false);
    } catch (error) {
      toast.error(error?.message || 'Impossible d’enregistrer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      icon={User}
      title="Mon compte"
      subtitle="Vos informations personnelles et votre rôle."
    >
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className="flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-full bg-primary-600 text-xl font-semibold text-white shadow-sm">
          {identity.initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {editing ? (
              <>
                <SettingsTextField
                  label="Nom"
                  compact
                  value={form.nom}
                  onChange={(nom) => setForm((prev) => ({ ...prev, nom }))}
                />
                <SettingsTextField
                  label="Prénom"
                  compact
                  value={form.prenom}
                  onChange={(prenom) => setForm((prev) => ({ ...prev, prenom }))}
                />
              </>
            ) : (
              <>
                <SettingsReadOnlyField label="Nom" value={getSessionNom(user)} compact />
                <SettingsReadOnlyField label="Prénom" value={getSessionPrenom(user)} compact />
              </>
            )}
            {editing ? (
              <SettingsTextField
                label="Email de connexion"
                compact
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(email) => setForm((prev) => ({ ...prev, email }))}
              />
            ) : (
              <SettingsReadOnlyField
                label="Email de connexion"
                value={identity.email}
                compact
              />
            )}
            {editing ? (
              <SettingsTextField
                label="Téléphone"
                compact
                type="tel"
                value={form.telephone}
                onChange={(telephone) => setForm((prev) => ({ ...prev, telephone }))}
              />
            ) : (
              <SettingsReadOnlyField label="Téléphone" value={identity.telephone} compact />
            )}
          </div>
          <div className="mt-3 max-w-md">
            <SettingsReadOnlyField
              label="Rôle"
              value={identity.roleLabel || formatRoleLabel(user?.role)}
              locked
              lockIcon={Lock}
              compact
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={saving}
                  className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={saving}
                  className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={startEdit}
                className="inline-flex items-center gap-2 rounded-xl border border-primary-200 bg-white px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
              >
                Modifier mes informations
              </button>
            )}
          </div>
        </div>
      </div>
      <p className="sr-only" data-testid="settings-account-identity">
        {getSessionDisplayName(user)}
      </p>
    </SettingsCard>
  );
}

function NotificationsCard() {
  return (
    <SettingsCard
      icon={Bell}
      title="Notifications"
      subtitle="Choisissez les alertes que vous souhaitez recevoir."
    >
      <SoundPreferencesControls
        commercantId={null}
        variant="platform"
        enableLabel="Notifications sonores"
      />
    </SettingsCard>
  );
}

function SecurityCard({ email }) {
  return (
    <SettingsCard
      icon={Shield}
      title="Sécurité"
      subtitle="Protégez votre compte et gérez vos accès."
    >
      <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-100">
        <Link
          to="/mot-de-passe-oublie"
          className="flex items-center justify-between gap-3 px-3 py-3 hover:bg-gray-50"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <RefreshCw className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Réinitialiser mon mot de passe</p>
              <p className="text-xs text-gray-500">
                Recevez un lien sécurisé par email pour définir un nouveau mot de passe.
              </p>
              {email ? (
                <p className="mt-0.5 text-xs text-gray-400">{email}</p>
              ) : null}
            </div>
          </div>
          <span className="text-sm font-medium text-primary-700">Ouvrir</span>
        </Link>
      </div>
    </SettingsCard>
  );
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState('compte');
  const user = useAdminSessionUser();
  const email = getSessionEmail(user);

  return (
    <div className="settings-page settings-page--platform -mx-4 min-h-[70vh] px-4 py-2 sm:-mx-6 sm:px-6" data-settings-chrome="platform">
      <SettingsPageHeader
        title="Paramètres Clickorico"
        subtitle="Gérez votre compte, vos notifications, l'équipe Clickorico et les contenus publics."
        kicker="Plateforme"
        badge="SUPER ADMIN"
        leadingIcon={Shield}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {activeTab === 'compte' ? <AccountCard user={user} /> : null}
      {activeTab === 'notifications' ? <NotificationsCard /> : null}
      {activeTab === 'securite' ? <SecurityCard email={email} /> : null}
      {activeTab === 'equipe' ? <Navigate to="/nr2j/equipe" replace /> : null}
      {activeTab === 'site-public' ? <SitePublicSettings /> : null}
      {activeTab === 'nr2j-banque' ? <Nr2jBankSettings /> : null}
    </div>
  );
}
